/**
 * Organization Section
 * Sub-component of PreferencesModal for enterprise organization code input and join flow.
 */
import { useState } from 'react';
import { api } from '../../services/api';
import { Loader2, Building2, Users, CheckCircle, AlertCircle } from 'lucide-react';
import type { UserPreferences } from '../../types';

export interface OrganizationSectionProps {
  preferences: UserPreferences;
  onPreferencesChange: (updated: UserPreferences) => void;
}

export function OrganizationSection({ preferences, onPreferencesChange }: OrganizationSectionProps) {
  const [showJoinOrganization, setShowJoinOrganization] = useState(false);
  const [organizationCode, setOrganizationCode] = useState('');
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [organizationSuccess, setOrganizationSuccess] = useState(false);

  async function handleJoinOrganization() {
    // Reset states
    setOrganizationError(null);
    setOrganizationSuccess(false);

    // Validation
    if (!organizationCode.trim()) {
      setOrganizationError('Organization code is required.');
      return;
    }
    if (organizationCode.trim().length < 6) {
      setOrganizationError('Organization code must be at least 6 characters.');
      return;
    }

    setOrganizationLoading(true);
    try {
      const result = await api.joinOrganization(organizationCode.trim());
      setOrganizationSuccess(true);
      setOrganizationCode('');
      setShowJoinOrganization(false);
      // Update local preferences with organization info
      onPreferencesChange({
        ...preferences,
        org_id: result.organization.org_id,
        org_name: result.organization.org_name,
        plan: 'enterprise',
        status: 'active',
      });
      // Clear success message after 3 seconds
      setTimeout(() => setOrganizationSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to join organization:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError?.response?.data?.message || 'Failed to join organization. Please check the code and try again.';
      setOrganizationError(errorMessage);
    } finally {
      setOrganizationLoading(false);
    }
  }

  function resetOrganizationForm() {
    setShowJoinOrganization(false);
    setOrganizationCode('');
    setOrganizationError(null);
  }

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
      <div className="flex items-center mb-3">
        <Building2 className="w-4 h-4 text-gray-500 mr-2" />
        <span className="text-sm font-medium text-gray-700">Organization</span>
        {preferences.org_id && (
          <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
        )}
      </div>

      {organizationSuccess && (
        <div className="flex items-center p-2 bg-green-50 text-green-700 rounded-lg text-sm mb-3">
          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          Successfully joined organization!
        </div>
      )}

      {preferences.org_id ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-blue-500 mr-2" />
              <div>
                <span className="text-sm font-medium text-blue-700">Member</span>
                {preferences.org_name && (
                  <p className="text-xs text-blue-600">{preferences.org_name}</p>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center">
            Contact your organization administrator to leave.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 mb-3">
            Have an organization code? Join to access enterprise features and billing.
          </p>

          {!showJoinOrganization ? (
            <button
              onClick={() => setShowJoinOrganization(true)}
              className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center justify-center"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Join Organization
            </button>
          ) : (
            <div className="space-y-3">
              {organizationError && (
                <div className="flex items-center p-2 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {organizationError}
                </div>
              )}

              <div>
                <label className="block text-xs text-gray-600 mb-1">Organization Code</label>
                <input
                  type="text"
                  value={organizationCode}
                  onChange={(e) => setOrganizationCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 uppercase"
                  placeholder="Enter code (e.g., ABC123XYZ)"
                  maxLength={20}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                />
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={resetOrganizationForm}
                  disabled={organizationLoading}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleJoinOrganization}
                  disabled={organizationLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {organizationLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Building2 className="w-4 h-4 mr-2" />
                  )}
                  Join
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
